import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { HttpApiResponseInterface } from 'pf-frontend-common/src/module/http/api-response.interface';
import { JsonApiStore } from 'pf-frontend-common/src/module/json-api/store';
import { ApiServiceInterface } from 'pf-frontend-common/src/service/api/service.interface';

import { JwtTokenStore } from 'common/data/jwt/token/store';
import { PropertyReportAttachmentModel } from 'common/data/property-report/attachment/model';
import { PropertyReportClaimModel } from 'common/data/property-report/claim/model';
import { PropertyReportEligibilityModel } from 'common/data/property-report/eligibility/model';
import { PropertyReportModel } from 'common/data/property-report/model';
import { objectFilterNonOrEmptyValue } from 'common/helper/object/filter/non-or-empty-value';
import { ApiEndpointServiceInterface } from 'common/service/api-endpoint/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

export class PropertyReportStore {
  /**
   * Upload input field name
   */
  public static UPLOAD_FIELD_NAME: string = 'attachment[]';

  /**
   * API endpoints to connect with the backend
   */
  private endpoint: {
    createReport: DataKeyValueStringInterface;
    upload: DataKeyValueStringInterface;
    checkEligible: DataKeyValueStringInterface;
    submitClaim: DataKeyValueStringInterface;
  } = {
    createReport: {
      path: this.apiEndpointService.getPath('/property/{propertyId}/report'),
      method: 'POST',
    },
    upload: {
      path: this.apiEndpointService.getPath('/property/report/{reportId}/attachments'),
      method: 'POST',
    },
    checkEligible: {
      path: this.apiEndpointService.getPath(`/property/report/cmt/eligible-check`),
      method: 'POST',
    },
    submitClaim: {
      path: this.apiEndpointService.getPath(`/property/report/cmt/claim-transaction`),
      method: 'POST',
    },
  };

  /**
   * Constructor
   */
  constructor(
    private apiEndpointService: ApiEndpointServiceInterface,
    private apiService: ApiServiceInterface,
    private propertyReportJsonApiStore: JsonApiStore<PropertyReportModel>,
    private propertyAttachmentJsonApiStore: JsonApiStore<PropertyReportAttachmentModel>,
    private checkEligibleJsonApiStore: JsonApiStore<PropertyReportEligibilityModel>,
    private submitClaimJsonApiStore: JsonApiStore<PropertyReportClaimModel>,
    private userAuthenticationService: UserAuthenticationServiceInterface
  ) {}

  /**
   * Submit a property report
   */
  public createReport(propertyId: string, model: PropertyReportModel): Promise<PropertyReportModel> {
    return this.apiService
      .request(
        this.endpoint.createReport.method,
        this.endpoint.createReport.path.replace('{propertyId}', propertyId),
        objectFilterNonOrEmptyValue(model.serialize()),
        true,
        this.getApiHeaders(this.userAuthenticationService.getToken())
      )
      .then((httpResponse) => {
        const sync = this.propertyReportJsonApiStore.syncWithMeta(httpResponse.data);

        return <PropertyReportModel>sync.data;
      });
  }

  /**
   * Upload property report attachment
   */
  public uploadAttachment(reportId: string, file: File): Promise<HttpApiResponseInterface> {
    return this.apiService
      .multipartRequest(
        this.endpoint.upload.method,
        this.endpoint.upload.path.replace('{reportId}', reportId),
        PropertyReportStore.UPLOAD_FIELD_NAME,
        file
      )
      .then((httpResponse) => {
        this.propertyAttachmentJsonApiStore.syncWithMeta(httpResponse.data);

        return <HttpApiResponseInterface>httpResponse.data;
      });
  }

  public checkEligible(model: PropertyReportEligibilityModel): Promise<HttpApiResponseInterface> {
    return this.apiService
      .request(this.endpoint.checkEligible.method, this.endpoint.checkEligible.path, model.serialize())
      .then((httpResponse) => {
        this.checkEligibleJsonApiStore.syncWithMeta(httpResponse.data);

        return <HttpApiResponseInterface>httpResponse.data;
      });
  }

  public submitClaim(model: PropertyReportClaimModel): Promise<HttpApiResponseInterface> {
    return this.apiService
      .request(this.endpoint.submitClaim.method, this.endpoint.submitClaim.path, model.serialize())
      .then((httpResponse) => {
        this.submitClaimJsonApiStore.syncWithMeta(httpResponse.data);

        return <HttpApiResponseInterface>httpResponse.data;
      });
  }

  /**
   * @param token - Authentication token
   * @returns the API headers to attach to each request
   */
  private getApiHeaders(token: string): DataKeyValueStringInterface {
    const headers: DataKeyValueStringInterface = {};
    headers[JwtTokenStore.HEADER_JWT] = 'Bearer ' + token;

    return headers;
  }
}
